/**
 * ResultTimeline — the plain list of detections, read as a TRAIL rather than a gallery.
 *
 * ─── WHY THE LIST NEEDED A SECOND SHAPE ───
 * A page of cards answers "show me the matches". It does not answer the question an operator
 * actually arrives with when they type a plate or a name: *where did this thing go*. Twenty-four
 * cards in a grid carry that answer — every card names its camera and its time — but reading it
 * means holding two dozen timestamps in your head and sorting the cameras by them yourself. So
 * the same rows are laid out in time order with the movement between them written out: Gate 1 at
 * 09:12, then Reception four minutes later, then the lift lobby.
 *
 * The grid is not replaced. It is still the better shape for "what did these look like" — twelve
 * frames on one screen — so the two are a toggle and the choice is remembered.
 *
 * ─── IT READS LIKE TEXT, NOT LIKE A LEDGER ───
 * The stops used to be a single column: one full-width row each, a rail down the left, and the
 * move to the next stop written in the gutter between them. It read beautifully and it wasted
 * most of the screen — a stop is four short facts, so on any real monitor each row was a few
 * hundred pixels of content followed by a thousand of nothing, and a trail of twenty stops meant
 * scrolling past six.
 *
 * So the stops now FLOW: left to right, wrapping to the next line, in reading order. Five or six
 * fit across a desktop and the whole page of a trail is usually on one screen, which is the
 * difference between seeing a route and scrolling through one.
 *
 * Wrapping costs the two things the column gave away for free, and both are paid for explicitly:
 *   • THE SEQUENCE. A column is obviously ordered; a wrapped grid is only ordered if you know
 *     which way to read it. So every stop carries its position, the header names the direction,
 *     and the ends of the page keep their colour.
 *   • THE MOVE. It can no longer live in the gutter between two stops, so it moves INSIDE the
 *     stop it arrives at — "came from Reception · 4m earlier". Each stop now states where the
 *     subject came from (or went to, read forwards), which turns out to be the more useful
 *     sentence anyway: it survives being read out of order, and it is still there when the stop
 *     is the last one on its line.
 *
 * ─── WHAT A STOP SHOWS ───
 * Four facts, in the order an operator looks for them: WHERE (the camera), WHEN (the clock and
 * the dwell), WHERE FROM (the move), and the frame — which doubles as the way into the record
 * and carries the PLAYBACK control. Everything else a stop knows (its models, its zone, the exact
 * detection count) is in the title text or the record behind it, because a fifth fact on the card
 * is what turns a dense layout back into a wall.
 *
 * ─── A STOP, NOT A DETECTION ───
 * A camera running at 8fps produces forty rows for one person walking through its view, and forty
 * rows down a timeline is not a trail, it is a wall. Consecutive detections of the SAME subject at
 * the SAME camera are therefore folded into one STOP with a span and a dwell — which is exactly
 * what the Journey answer's server-side sessionization does, down to the ten-minute gap, so the
 * two agree about how many times somebody visited reception rather than disagreeing by one.
 *
 * Two things keep the fold honest:
 *   • The subject is part of the key. Two different people at one camera in the same minute are
 *     two stops, because merging them would invent a visit neither of them made. Unidentified
 *     people have a synthetic subject id from the projection, so this holds for them too.
 *   • A gap wider than the sessionization window starts a new stop, so somebody who comes BACK
 *     to a camera reads as a return rather than as one very long stay.
 *
 * ─── IT IS A PAGE, AND IT SAYS SO ───
 * This draws the rows the server sent for the CURRENT page — the newest 24 of however many
 * matched. That makes it a slice of a trail, not the trail, and the header says which. For the
 * whole thing there is the Journey answer, which sessionizes the entire match set server-side;
 * the link to it sits in this header for exactly that reason.
 *
 * ─── DIRECTION ───
 * The server pages newest-first, and the pager below reads "1–24 of 240" against that order, so
 * that is the default here too: later in the reading order is further back in time, and each
 * stop's move says so ("4m earlier · came from Gate 1"). Oldest-first is one click away for
 * reading a trail forwards, and reverses only the rows already on screen — it cannot reorder a
 * page it does not have.
 */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PhotoIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';
import type { SearchEventRow } from '../../api/contracts';
import { useAlertImage } from '../alerts/useAlertImage';
import { MODEL_LABEL, MODEL_SHORT, titleCase } from './searchModel';
import { fmtDuration } from './answers/common';

/**
 * The gap that ends a stop, in minutes.
 *
 * Matched to the command-center's own `SEARCH_JOURNEY_GAP_MINUTES` default rather than chosen
 * independently: this view and the Journey answer describe the same movement through the same
 * cameras, and two different ideas of "the same visit" would have them report different numbers
 * of stops for one person. The server sends its value as `gap_minutes` on a journey result, so
 * that is preferred when the caller has one.
 */
export const DEFAULT_GAP_MINUTES = 10;

/**
 * The track's fixed measurements, in px.
 *
 * A card is a FIXED WIDTH, not a stretched one, and that is the point. `minmax(x, 1fr)` cards
 * fill whatever the row has left, so a track that fits four and a half of them drew four cards
 * half again as wide as their own contents.
 *
 * 256px, up from 192. The card is no longer only a frame — where, when and what saw it are
 * printed UNDER the picture now rather than over it (see StopCard), and at 192 the camera name
 * was truncating to a word and a half on a line that is the first thing read. The frame grows
 * with it, 108px tall to 144, which is the difference between recognising somebody in it and
 * knowing that somebody is there.
 *
 * The slack goes into the GAPS instead, where it is not waste: the gap is the connector's
 * column, and a wider one lets the move read on a single line instead of clamping to two.
 * Bounded at both ends — below `MIN_GAP_PX` the sentence cannot be read, above `MAX_GAP_PX` the
 * ribbon stops looking like a link between two cards and starts looking like a gap between two
 * groups of them. Whatever is left over after the cap sits at the end of the row, exactly as it
 * does in any fixed-width card grid.
 *
 * Declared as NUMBERS because the column count and the gap both have to be known in JavaScript:
 * the connector is positioned into the gap, and the ribbon has to know where each row ends.
 */
const CARD_PX = 256;
const MIN_GAP_PX = 84;
const MAX_GAP_PX = 160;

/**
 * ── A STOP CARD SIZED TO THE ROOM THE TRACK HAS ──
 *
 * A stop is a 16:9 frame with a four-line caption under it, so a fixed 256px card is 218px tall
 * whatever the page can spare. With the path dock open under it — which is now the default,
 * because the route is the other half of this answer — that leaves one row of stops and half of
 * the next, and a trail read one row at a time is a trail nobody can see the shape of.
 *
 * The tiers give up caption lines before frame width, in that order: what a stop IS gets read
 * off the picture and the camera name, and the models and the count are already in the card's
 * hover text. `dense` therefore prints the two lines that cannot be inferred from anything else
 * and lets the frame stay big enough to recognise a person in.
 */
export type StopDensity = 'comfortable' | 'compact' | 'dense';

interface StopTier {
  density: StopDensity;
  /** The caption's height at this tier — the fixed part of a card. */
  caption: number;
  width: number;
}

const STOP_TIERS: StopTier[] = [
  { density: 'comfortable', caption: 74, width: CARD_PX },
  { density: 'compact', caption: 56, width: 208 },
  { density: 'dense', caption: 40, width: 168 },
];

/**
 * The stop card for a track of this height. Never GROWN past the comfortable size, only shrunk —
 * the same rule the search grid follows, and for the same reason: a short container producing a
 * bigger card is the defect, not the fix.
 */
export function fitStopCard(availableHeight: number): { width: number; density: StopDensity } {
  const ROW_GAP = 8;
  if (!Number.isFinite(availableHeight) || availableHeight <= 0) {
    return { width: CARD_PX, density: 'comfortable' };
  }

  // Two rows of stops is the shape of a trail: it is what makes a route visible as a route
  // rather than as a list that happens to wrap.
  const rows = availableHeight >= 900 ? 3 : 2;
  const rowHeight = (availableHeight - ROW_GAP * (rows + 1)) / rows;

  for (const tier of STOP_TIERS) {
    const frame = Math.min((tier.width * 9) / 16, rowHeight - tier.caption);
    if (frame < 56) continue;                       // below this the frame shows nothing
    const width = Math.round(Math.min(tier.width, (frame * 16) / 9));
    if (width < 140) continue;                      // the camera name stops being readable
    return { width, density: tier.density };
  }
  return { width: STOP_TIERS[STOP_TIERS.length - 1].width, density: 'dense' };
}

/**
 * The track before it has been measured, and anywhere it cannot be.
 *
 * Deliberately NOT the fixed-width layout: with no measurement there is no gap to position a
 * connector into, so this is a plain stretching card grid — a correct picture of the same stops,
 * without a ribbon drawn from guessed geometry.
 */
const GRID_AUTO = 'grid gap-x-6 gap-y-2 [grid-template-columns:repeat(auto-fill,minmax(16rem,1fr))]';

interface Track {
  /** Cards across, or 0 until the track has been measured. */
  cols: number;
  /** The column gap, which is also the connector's width. */
  gap: number;
}

/**
 * How many cards fit across the track, and how much room that leaves between them.
 *
 * The column count is the one thing CSS knows and JavaScript does not, and the ribbon cannot do
 * without it: a trail drawn across a wrapping layout has to say where each row ENDS — that is
 * where it stops running sideways and turns down to the next line — and getting it wrong would
 * draw a connector pointing at a card that is somewhere else entirely.
 *
 * Returns `cols: 0` until there is a measurement. The caller then draws `GRID_AUTO` with no
 * connectors, which is a correct picture rather than a guessed one.
 */
function useTrack(ref: React.RefObject<HTMLElement>, cardPx: number): Track {
  const [track, setTrack] = useState<Track>({ cols: 0, gap: MIN_GAP_PX });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      const width = el.clientWidth;
      if (!width) return;
      const cols = Math.max(1, Math.floor((width + MIN_GAP_PX) / (cardPx + MIN_GAP_PX)));
      // One column has no gaps to share the slack out into; the card simply takes the width.
      const spare = cols > 1 ? (width - cols * cardPx) / (cols - 1) : MIN_GAP_PX;
      const gap = Math.round(Math.min(MAX_GAP_PX, Math.max(MIN_GAP_PX, spare)));
      setTrack((prev) => (prev.cols === cols && prev.gap === gap ? prev : { cols, gap }));
    };
    measure();

    // ResizeObserver catches the cases a window listener misses and this view actually hits: the
    // filter bar wrapping to a second line, and the cloud's node panel opening beside the page.
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [ref, cardPx]);

  return track;
}

/** One detection, carrying its position in the page so the record modal can page from it. */
interface Hit {
  row: SearchEventRow;
  /** Index into the ROWS AS THE SERVER SENT THEM — what `onOpen` expects, see the note there. */
  index: number;
  at: number;
}

/** One visit: a subject, at a camera, over a span. */
export interface Stop {
  key: string;
  cameraId: string;
  subjectId: string;
  subjectLabel: string | null;
  subjectKind: string | null;
  isKnown: boolean;
  /**
   * Whether this stop is about a SUBJECT at all.
   *
   * A fire, an intrusion or a people-count has nobody to follow — the projection leaves both
   * `subject_id` and `track_id` null — so those stops are keyed by camera to keep the fold
   * working. That key must never be counted as an identity: without this flag a search for
   * fires across five cameras reported "5 subjects", which is five more than it found.
   */
  tracked: boolean;
  /**
   * Whether this stop carries an identity that MEANS THE SAME THING AT ANOTHER CAMERA — a
   * registered person, a read plate. See `isRealIdentity`: NOT merely "has a subject_id", which
   * every row does.
   *
   * `tracked` is not that, and conflating the two is what confined the tracking path to name and
   * plate searches. A tracker id is unique only within one camera, so an attribute search ("red
   * top, black trousers") produces a different key at every camera the person walks past — and
   * counting those keys as subjects turned one person's route into "6 different subjects" and
   * refused to draw it. The route was the only thing the operator came to the Track tab for.
   *
   * So the count that decides whether a line may be drawn is over THIS flag: two enrolled people
   * are provably two travellers and must never be joined; six anonymous tracks matching one
   * description are one description, which is the most the system can say and exactly what was
   * asked for.
   */
  identified: boolean;
  zone: string | null;
  from: number;
  to: number;
  /** Always oldest-first, whichever way the page is read — a visit happened forwards. */
  hits: Hit[];
  models: string[];
  /** 1-based position in TIME order, within this page. */
  ordinal: number;
}

export interface ResultTimelineProps {
  /** The page's event rows, in the server's order (newest first). */
  rows: SearchEventRow[];
  loading?: boolean;
  /** Which way to read the page. See the direction note in the file header. */
  order: 'asc' | 'desc';
  cameraName: (id: string) => string | undefined;
  /** A frame as a directly-loadable URL (local host). */
  imageFor?: (row: SearchEventRow) => string | null;
  /** Lazy frame resolver, for a host that streams frames through a tunnel (cloud). */
  loadImage?: (row: SearchEventRow) => Promise<string | null>;
  /**
   * Open one detection's record.
   *
   * Takes the index into `rows` rather than the row, because that is what the page's modal pages
   * through — and it is why a stop holds its hits' original indices instead of renumbering them.
   */
  onOpen: (index: number) => void;
  /** Play the footage around one detection. Omit to offer no playback control. */
  onPlay?: (row: SearchEventRow) => void;
  /** Whether a row can be played at all — a row with no usable timestamp cannot. */
  playable?: (row: SearchEventRow) => boolean;
  /** Tells "nothing matched" from "no access", as every other answer does. */
  cameraScope?: number;
  /** Total matches, so a page can say it is one. */
  total?: number;
  /** The sessionization gap the server used, when the caller knows it. */
  gapMinutes?: number;
  /** Switch the question to Journey — the whole trail, sessionized server-side. */
  onFollowTrail?: () => void;
}

/**
 * Which subject keys are a REAL identity, and which the projection minted because there was none.
 *
 * The node computes `subject_id` by precedence (see the command-center's searchProjection):
 *
 *     plate → person id → `t:<camera>:<track>` → `e:<event>`
 *
 * The first two mean the same thing at a second camera. The last two do not: a tracker id is
 * unique only within its own camera, and an event key is unique to one detection. They are still
 * subject ids — they have to be, so the fold and the distinct-subject count work — but reading
 * them as identities is what confined the tracking path to name and plate searches. An attribute
 * query ("red top, black trousers") gets a fresh `t:` key at every camera, so counting those as
 * identities turned one person's walk through a building into "6 different subjects" and refused
 * to draw the route.
 *
 * The prefixes are the contract, and they are asserted in `scripts/test-tracking-path.ts` against
 * the shapes the projection actually emits — an earlier version of this check tested
 * `subject_id != null`, which is true of every row the backend has ever produced and therefore
 * fixed nothing.
 */
const SYNTHETIC_SUBJECT = /^(t:|e:)/;

export function isRealIdentity(subjectId: unknown): boolean {
  return typeof subjectId === 'string' && subjectId !== '' && !SYNTHETIC_SUBJECT.test(subjectId);
}

/** A subject key that never merges two different people — see the file header. */
function subjectKeyOf(row: SearchEventRow): string {
  if (row.subject_id) return String(row.subject_id);
  // No identity at all (fire, a people-count, an intrusion): keyed by camera, so the fold still
  // collapses a burst at one camera without ever pulling two cameras together.
  if (row.track_id === null || row.track_id === undefined) return `cam:${row.camera_id}`;
  // A tracker id is unique only within one camera, which is why the camera is in the key.
  return `trk:${row.camera_id}:${row.track_id}`;
}

/**
 * Fold the page's rows into stops.
 *
 * EXPORTED because the tracking path dock draws the same stops this list prints, and a second
 * fold with its own idea of where a visit ends would put a pin on the plan for a stop that is
 * not in the list beside it.
 *
 * Runs over rows sorted into TIME order regardless of how the page is read: the fold is about
 * what happened, and folding a reversed list would merge the end of one visit with the start of
 * the one before it. The display order is applied afterwards.
 */
export function buildStops(rows: SearchEventRow[], gapMs: number): { stops: Stop[]; undated: number } {
  const hits: Hit[] = [];
  let undated = 0;

  rows.forEach((row, index) => {
    const at = Date.parse(row.event_timestamp);
    if (!Number.isFinite(at)) { undated += 1; return; }
    hits.push({ row, index, at });
  });

  hits.sort((a, b) => a.at - b.at);

  const stops: Stop[] = [];
  for (const hit of hits) {
    const subjectId = subjectKeyOf(hit.row);
    const current = stops[stops.length - 1];
    const continues = current
      && current.cameraId === hit.row.camera_id
      && current.subjectId === subjectId
      && hit.at - current.to <= gapMs;

    if (continues) {
      current.to = hit.at;
      current.hits.push(hit);
      if (!current.models.includes(hit.row.model_key)) current.models.push(hit.row.model_key);
      // A burst of person detections where only the face event carried the name: the stop takes
      // the name from whichever row has one rather than from the first row alone.
      if (!current.subjectLabel && hit.row.subject_label) current.subjectLabel = hit.row.subject_label;
      if (hit.row.is_known) current.isKnown = true;
      continue;
    }

    stops.push({
      key: `${hit.row.camera_id}|${subjectId}|${hit.at}`,
      cameraId: hit.row.camera_id,
      subjectId,
      subjectLabel: hit.row.subject_label || null,
      subjectKind: hit.row.subject_kind || null,
      isKnown: !!hit.row.is_known,
      tracked: !!hit.row.subject_id || hit.row.track_id !== null,
      identified: isRealIdentity(hit.row.subject_id),
      zone: hit.row.zone || null,
      from: hit.at,
      to: hit.at,
      hits: [hit],
      models: [hit.row.model_key],
      ordinal: stops.length + 1,
    });
  }

  return { stops, undated };
}

/** A wall clock reading from an epoch — the stops hold millis, not the rows' ISO strings. */
const clock = (t: number) => new Date(t).toLocaleTimeString([], {
  hour: '2-digit', minute: '2-digit', hour12: false,
});

/**
 * The calendar day a stop belongs to, as a stable key.
 *
 * Compared instead of the LABEL, which is what the boundary test used to compare. Two different
 * days can carry the same label — "Thu 18 Sep" of this year and of last — and a search window
 * wide enough to hold both would have read them as one day, silently merging two sections and
 * putting the row breaks in the wrong places.
 */
const dayKey = (t: number) => new Date(t).toDateString();

/** "Today" / "Yesterday" / "Thu 18 Sep" — the day a run of stops belongs to. */
function dayLabel(t: number): string {
  const d = new Date(t);
  const today = new Date();
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return dayDate(t);
}

/** "Thu 18 Sep" — the calendar reading, always, whatever the relative label says. */
function dayDate(t: number): string {
  return new Date(t).toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short' });
}

/**
 * The ribbon between two stops standing side by side in one row — and the move written ON it.
 *
 * THE SENTENCE BELONGS TO THE LINE, NOT TO EITHER CARD. "to exit · 48s later" is not a fact
 * about the stop at the exit or about the one before it; it is the fact about the space between
 * them, which is exactly where the eye looks when following a trail. It used to be the third
 * line inside the card, where it read as another of that stop's properties, cost a line on every
 * card, and left the connector a bare tick that could have meant anything.
 *
 * Lives in the GUTTER — absolutely positioned out of the card and into the grid's column gap —
 * so it costs no layout and cannot push a card out of its track. The gap is sized for it
 * (`COL_GAP_PX`): the connector has a column of its own in all but name.
 *
 * THE ARROWHEAD FOLLOWS TIME, NOT THE READING ORDER. Reading newest-first, the card to the right
 * happened EARLIER, so the subject travelled right-to-left and the chevron points left. Pointing
 * it along the reading order instead would put a → beside the words "came from", and one of the
 * two would be a lie.
 *
 * Dashed when the camera did not change: the subject was seen at the same place again, which is
 * a gap in one visit rather than a journey between two.
 */
function InlineLink({ order, moved, text, gap }: {
  order: 'asc' | 'desc'; moved: boolean; text: string;
  /** The measured column gap — the connector spans it exactly, so the two cannot disagree. */
  gap: number;
}) {
  const line = moved
    ? <span className="h-px flex-1 bg-border" />
    : <span className="h-0 flex-1 border-t border-dashed border-border" />;
  const head = order === 'asc'
    ? <ChevronRightIcon className="h-3 w-3 shrink-0 text-muted-foreground" />
    : <ChevronLeftIcon className="h-3 w-3 shrink-0 text-muted-foreground" />;

  return (
    <span
      aria-hidden="true"
      style={{ right: -gap, width: gap }}
      className="pointer-events-none absolute inset-y-0 flex flex-col items-center justify-center gap-1"
    >
      <span className="flex w-full items-center">
        {order === 'asc' ? <>{line}{head}</> : <>{head}{line}</>}
      </span>
      {/* Two lines at most. A camera with a long name would otherwise push the label taller than
          the card it sits beside and drag the whole row down with it. */}
      <span className="line-clamp-2 px-0.5 text-center text-[10px] leading-tight text-muted-foreground">
        {text}
      </span>
    </span>
  );
}

/**
 * The turn at the end of a row — the trail leaving the right edge and coming back in at the left
 * of the line below, carrying the same sentence its sideways twin does.
 *
 * A full-width band between two rows rather than a shape hung off the corner of a card. A corner
 * elbow has to guess where the next row's first card starts, and it guesses wrong the moment a
 * card is a different height or the row is short; a band spans the same track the cards do, so
 * its two ends are the two corners it is actually joining.
 *
 * The sweep is drawn as TWO segments with the label between them rather than one line behind a
 * masked label: a mask has to know the colour it is painting over, and this band sits on
 * whatever surface the host gives the results area.
 *
 * Decorative — `aria-hidden`. The same sentence is in the title text of the stop it belongs to,
 * so a screen reader loses nothing and gains one less list item to step through.
 */
function WrapLink({ order, text }: { order: 'asc' | 'desc'; text: string }) {
  return (
    <li aria-hidden="true" className="relative col-span-full h-5 list-none">
      {/* The sweep back across the track, split around the label… */}
      <span className="absolute left-2 right-[calc(50%+4.5rem)] top-1/2 h-px bg-border" />
      <span className="absolute left-[calc(50%+4.5rem)] right-2 top-1/2 h-px bg-border" />
      {/* …with a stub up into the row that just ended, and one down into the row that starts. */}
      <span className="absolute right-2 top-0 h-1/2 w-px bg-border" />
      <span className="absolute bottom-0 left-2 h-1/2 w-px bg-border" />
      {/* Same rule as InlineLink: the head marks where time is going, which reading newest-first
          is back up into the row above. */}
      {order === 'asc'
        ? <ChevronDownIcon className="absolute -bottom-1 left-[2px] h-3 w-3 text-muted-foreground" />
        : <ChevronUpIcon className="absolute -top-1 right-[2px] h-3 w-3 text-muted-foreground" />}
      <span className="absolute left-1/2 top-1/2 max-w-[9rem] -translate-x-1/2 -translate-y-1/2 truncate text-center text-[10px] leading-tight text-muted-foreground">
        {text}
      </span>
    </li>
  );
}

/** What a stop says about the move that reaches it, and which way that move points. */
interface Move {
  text: string;
  /** Back in time (reading newest-first) or forwards. Null for the end of the page. */
  direction: 'back' | 'forward' | null;
}

/**
 * One stop — a card that IS its frame, with everything it says written over the top.
 *
 * THE PICTURE IS THE POINT. A stop was a small thumbnail with three lines of text beside it,
 * which spent most of a card on words and left the one thing an operator actually recognises a
 * stop by — the face, the shirt, the van — at 96px wide. The frame now takes the whole card and
 * the words sit on it: an index, the camera, the clock, the playback control. Nothing was
 * dropped; it stopped being laid out beside the image and started being laid out on it.
 *
 * LEGIBILITY OVER AN ARBITRARY IMAGE is the thing this has to get right, because the picture
 * underneath is whatever the camera saw — a white wall, a floodlit forecourt, anything. So no
 * text sits directly on the frame: the bottom strip is a gradient to black and the corner marks
 * are pills, both at an opacity that survives a bright frame without hiding what is behind it.
 *
 * Its own component because of the image hook: `useAlertImage` is per-frame, and a hook cannot
 * be called from inside a `.map` in the parent.
 *
 * TWO CONTROLS, NOT ONE, and they are separate elements rather than one card with a click
 * handler: the card opens the record, the button in the middle plays the footage, and a button
 * inside a button is invalid HTML that keyboard users experience as a control they cannot reach.
 */
function StopCard({
  stop,
  density,
  cameraLabel,
  ordinal,
  move,
  subjectNote,
  accent,
  link,
  order,
  imageFor,
  loadImage,
  onOpen,
  onPlay,
}: {
  stop: Stop;
  /** How much caption this card may print — see `fitStopCard`. */
  density: StopDensity;
  cameraLabel: string;
  /** The stop's position on the page, or null when the page is not one subject's trail. */
  ordinal: number | null;
  move: Move;
  /** Who this stop was about — only when the page holds more than one subject. */
  subjectNote: string | null;
  /** The chronological ends of the page, which keep their colour from the old rail. */
  accent: 'first' | 'last' | null;
  /**
   * The ribbon to the card beside this one, when there IS one beside it.
   *
   * Null at the end of a row (the turn is drawn by `WrapLink` instead), at the end of the page,
   * and before a day heading — where a connector would claim a continuity the break denies.
   */
  link: { moved: boolean; text: string; gap: number } | null;
  order: 'asc' | 'desc';
  imageFor?: (row: SearchEventRow) => string | null;
  loadImage?: (row: SearchEventRow) => Promise<string | null>;
  onOpen: (index: number) => void;
  onPlay?: () => void;
}) {
  const lead = stop.hits[0];
  const { src, markFailed, ref } = useAlertImage(lead.row, imageFor, loadImage);

  const dwell = (stop.to - stop.from) / 1000;
  const detections = stop.hits.length;
  /** On the frame: when it started, and how long it lasted. */
  const when = dwell >= 1 ? `${clock(stop.from)} · ${fmtDuration(dwell)}` : clock(stop.from);
  /** The full reading, for the hover text, where there is room for it. */
  const whenFull = dwell >= 1
    ? `${clock(stop.from)} → ${clock(stop.to)} · ${fmtDuration(dwell)}`
    : clock(stop.from);

  /**
   * The fuller reading, on hover — what the caption abbreviates and what it has no room for.
   *
   * The caption now prints the camera, the position, the start and dwell, the models and the
   * count, so this is no longer the only place those appear. What is still ONLY here is the
   * zone, the exact start → end range behind the dwell, and the move that reached this stop —
   * each of which can decide whether a hit is the one being looked for, and none of which
   * earns a line on a card that has to stay one glance wide.
   */
  const title = [
    cameraLabel,
    stop.zone ? `zone ${stop.zone}` : null,
    whenFull,
    `${detections} ${detections === 1 ? 'detection' : 'detections'}`,
    stop.models.map((m) => MODEL_LABEL[m] || titleCase(m)).join(' + '),
    subjectNote,
    move.direction ? move.text : null,
  ].filter(Boolean).join(' · ');

  return (
    <li className="relative">
      {link && <InlineLink order={order} moved={link.moved} text={link.text} gap={link.gap} />}

      {/*
        ── WHERE AND WHEN ARE UNDER THE PICTURE, NOT ON IT ──

        They were scrims over the frame: a gradient at the top carrying the position, the camera
        and the time, another at the bottom carrying the models and the count. Two things were
        wrong with that. The frame is whatever the camera saw, so text over it is legible above a
        doorway and gone above a floodlit forecourt — a scrim dark enough to fix that is a scrim
        covering a third of a 108px picture. And the facts are the reason the card is read at
        all: which camera, at what time, in what order. Printing them over the evidence made the
        card choose between showing the evidence and being readable.

        So the card is a frame AND a caption now. The picture is clean edge to edge, the words sit
        on the card's own surface where the theme guarantees their contrast, and the play control
        keeps the one corner of the frame that never had writing in it.
      */}
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="relative">
          <button
            ref={ref}
            type="button"
            onClick={() => onOpen(lead.index)}
            title={`${title} — open this detection`}
            aria-label={`Open the detection at ${cameraLabel}, ${clock(stop.from)}`}
            className="relative block aspect-[16/9] w-full overflow-hidden bg-neutral-900 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand"
          >
            <span className="absolute inset-0 bg-gradient-to-br from-neutral-800 to-neutral-950" />
            {src ? (
              <img
                src={src}
                alt=""
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover"
                onError={markFailed}
              />
            ) : (
              <PhotoIcon className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 text-white/20" />
            )}

            {/* The colour the rail used to carry, down the frame's leading edge. On the FRAME
                rather than the card, because it marks the ends of the page's chronology and the
                picture is what the eye scans for them. */}
            {accent && (
              <span
                className={`absolute inset-y-0 left-0 w-[3px] ${accent === 'first' ? 'bg-emerald-500' : 'bg-brand'}`}
                title={accent === 'first' ? 'Earliest on this page' : 'Latest on this page'}
              />
            )}
          </button>

          {/*
            PLAYBACK, over the frame's bottom-right — the corner a player's controls live in
            everywhere else, and now the only thing drawn on the picture at all.

            A SIBLING of the card button, never a child: a button inside a button is invalid HTML
            that keyboard users experience as a control they cannot reach. It is positioned
            against this wrapper rather than the whole card, or it would float over the caption.
          */}
          {onPlay && (
            <button
              type="button"
              onClick={onPlay}
              title="Play the recording around the start of this stop"
              aria-label={`Play the recording at ${cameraLabel}, ${clock(stop.from)}`}
              className="absolute bottom-1 right-1 flex h-6 w-6 items-center justify-center rounded bg-black/55 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            >
              <PlayCircleIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/*
          THE CAPTION, in the order the stop is read: which stop on the trail, where, when, and
          what saw it. The ordinal leads because on a trail the position is the first question —
          "third stop" is what an operator says out loud — and it is absent where there is no
          trail to be third on (see the call site).
        */}
        {/* The caption sheds lines before the frame sheds pixels — the models and the count are
            in this card's hover text either way, and WHICH CAMERA and WHEN are not inferable
            from anything else on screen. See `fitStopCard`. */}
        <div className={`border-t border-border ${density === 'comfortable' ? 'px-2 py-1.5' : 'px-1.5 py-1'}`}>
          <p className="flex items-baseline gap-1.5">
            {ordinal !== null && (
              <span className={`shrink-0 font-semibold tabular-nums text-brand ${density === 'dense' ? 'text-[10px]' : 'text-[11px]'}`}>
                {ordinal}
              </span>
            )}
            <span className={`min-w-0 flex-1 truncate font-medium leading-tight text-foreground ${density === 'dense' ? 'text-[11px]' : 'text-[12px]'}`}>
              {cameraLabel}
            </span>
          </p>
          <p className={`truncate leading-tight tabular-nums text-muted-foreground ${density === 'dense' ? 'text-[10px]' : 'text-[11px]'}`}>
            {when}
          </p>
          {subjectNote && density !== 'dense' && (
            <p className="truncate text-[10px] leading-tight text-muted-foreground">{subjectNote}</p>
          )}
          {density === 'comfortable' && (
            <p className="truncate text-[10px] leading-tight text-muted-foreground">
              {stop.models.map((m) => MODEL_SHORT[m] || MODEL_LABEL[m] || titleCase(m)).join(' + ')}
              {detections > 1 && <span className="tabular-nums"> · ×{detections}</span>}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

export default function ResultTimeline({
  rows,
  loading = false,
  order,
  cameraName,
  imageFor,
  loadImage,
  onOpen,
  onPlay,
  playable,
  cameraScope,
  total,
  gapMinutes = DEFAULT_GAP_MINUTES,
  onFollowTrail,
}: ResultTimelineProps) {
  const trackRef = useRef<HTMLOListElement>(null);

  /**
   * The height the answer area has left for this track, and the card size that follows from it.
   *
   * Measured off the scrolling container rather than the window: what this list actually gets is
   * whatever the filter panel above and the path dock below have not taken, and both of those
   * move under the operator — the dock is dragged, the panel folds.
   */
  const rootRef = useRef<HTMLDivElement>(null);
  const [boxHeight, setBoxHeight] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current?.parentElement;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const h = entry.contentRect.height;
      // Sub-pixel churn is not a change worth a render, and re-rendering from inside a
      // ResizeObserver on every tick is how an observer loop starts.
      setBoxHeight((prev) => (Math.abs(h - prev) < 4 ? prev : h));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const stopCard = useMemo(() => fitStopCard(boxHeight), [boxHeight]);

  /** `cols: 0` until the track has been measured — see `useTrack`. */
  const { cols, gap } = useTrack(trackRef, stopCard.width);

  const { stops, undated } = useMemo(
    () => buildStops(rows, Math.max(1, gapMinutes) * 60_000),
    [rows, gapMinutes],
  );

  /** The stops in reading order. The fold itself always ran forwards — see `buildStops`. */
  const shown = useMemo(
    () => (order === 'asc' ? stops : [...stops].reverse()),
    [stops, order],
  );

  /**
   * Which stops finish a visual row — where the ribbon has to turn down instead of running on.
   *
   * Walked rather than taken as `i % cols`, because a day heading spans the full track and
   * therefore ENDS the row it lands in. Counting straight through one would put every later
   * turn in the wrong place, and a connector in the wrong place points at a card the subject
   * was never at.
   *
   * All false until the track has been measured, which is what leaves the first paint
   * connectorless rather than connected wrongly.
   */
  const rowEnds = useMemo(() => {
    const flags: boolean[] = new Array(shown.length).fill(false);
    if (!cols) return flags;
    let col = 0;
    shown.forEach((stop, i) => {
      if (i > 0 && dayKey(stop.from) !== dayKey(shown[i - 1].from)) col = 0;
      col += 1;
      if (col === cols) { flags[i] = true; col = 0; }
    });
    return flags;
  }, [shown, cols]);

  /**
   * Whether this page is about ONE subject.
   *
   * It decides two things: whether a stop needs to name who it was (pointless when every stop is
   * the same person, essential when they are not), and whether the stops can be numbered — an
   * ordinal is a position on a trail, and a trail needs a single traveller to be a trail.
   */
  const subjects = useMemo(
    () => new Set(stops.filter((s) => s.tracked).map((s) => s.subjectId)),
    [stops],
  );
  /**
   * The identities — people and plates the system can recognise at a second camera. See
   * `Stop.identified`: these are what may not be joined, and tracker keys are not among them.
   */
  const identities = useMemo(
    () => new Set(stops.filter((s) => s.identified).map((s) => s.subjectId)),
    [stops],
  );
  /**
   * One traveller, so the stops form a trail and may be numbered.
   *
   * Two or more identities is provably several people. Zero identities with several tracker keys
   * is ONE DESCRIPTION — every stop matched the filters, and whether they are the same person is
   * a question the system cannot answer either way. Numbering those is right: the page is
   * already the answer to "where did this description go", and refusing the ordinals only made
   * the answer harder to read without making it more true. `byDescription` is what says so out
   * loud.
   */
  const oneSubject = identities.size <= 1 && stops.every((s) => s.tracked);
  const byDescription = oneSubject && identities.size === 0 && subjects.size > 1;
  const sole = oneSubject && identities.size === 1 ? stops.find((st) => st.identified) || stops[0] : null;
  /** What to call that subject: its own name or plate, or the nearest honest description. */
  const soleNoun = !sole ? ''
    : stops.find((st) => st.subjectLabel)?.subjectLabel
      || (sole.subjectKind === 'vehicle' ? 'A vehicle, no plate read'
        : sole.isKnown ? 'An enrolled person' : 'An unidentified person');

  /**
   * Stops per calendar day, for the day headings.
   *
   * Counted once here rather than by scanning forward from each heading as it renders, which
   * would be a second pass over the page for every day on it — and computed from `stops`, not
   * from the rows, so the number means the same thing as the cards underneath it.
   */
  const dayStops = useMemo(() => {
    const counts = new Map<string, number>();
    for (const stop of stops) {
      const key = dayKey(stop.from);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return counts;
  }, [stops]);

  const cameraCount = useMemo(() => new Set(stops.map((s) => s.cameraId)).size, [stops]);
  const label = (id: string) => cameraName(id) || id.slice(0, 8);

  /*
   * Skeleton for the whole of a refetch, not just the first load — and deliberately the same
   * rule the card grid follows. Leaving the previous query's stops on screen while a new one is
   * in flight would present them as the answer to the new question, which for a trail ("did they
   * go to the server room") is a far more dangerous kind of stale than a stale gallery.
   */
  if (loading) {
    return (
      <div className={`${GRID_AUTO} p-4`}>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="aspect-[16/9] animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    );
  }

  if (!stops.length) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-16 text-center">
        <MagnifyingGlassIcon className="h-8 w-8 text-muted-foreground opacity-40" />
        <p className="text-sm font-medium text-foreground">
          {cameraScope === 0 ? 'No cameras in scope' : 'Nothing matched'}
        </p>
        <p className="text-[13px] text-muted-foreground">
          {cameraScope === 0
            ? 'You do not have access to any camera matching these filters.'
            : 'Try widening the time window, or removing an appearance filter.'}
        </p>
      </div>
    );
  }

  const firstInTime = stops[0];
  const lastInTime = stops[stops.length - 1];
  const partial = typeof total === 'number' && total > rows.length;

  return (
    <div ref={rootRef} className="p-4">
      {/* ── What this trail is, in one line, before the stops themselves ── */}
      <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2 className="text-sm font-semibold text-foreground">
          {sole && `${soleNoun} — `}
          {stops.length} {stops.length === 1 ? 'stop' : 'stops'} across {cameraCount}{' '}
          {cameraCount === 1 ? 'camera' : 'cameras'}
          {/* Only when there is more than one, and only counting stops that HAVE a subject —
              see `tracked`. Zero of them (a fire search) says nothing about subjects at all. */}
          {!oneSubject && identities.size > 1 && `, ${identities.size} subjects`}
        </h2>
        {/* The honest caveat for a trail built out of a description rather than an identity.
            Said once, beside the heading, rather than repeated on every card. */}
        {byDescription && (
          <span className="text-[11px] text-muted-foreground">
            · {subjects.size} separate tracks matching this description — not confirmed as one
          </span>
        )}
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {clock(firstInTime.from)} → {clock(lastInTime.to)}
        </span>
        {/*
          A single column is obviously in order; a wrapped one is only in order if you know which
          way to read it. Said here, next to the span it applies to, rather than left to be
          inferred from the timestamps on the cards — inferring it is exactly the work this view
          exists to save.
        */}
        {stops.length > 1 && (
          <span className="text-[11px] text-muted-foreground">
            · {order === 'asc' ? 'oldest first' : 'newest first'}, left to right
          </span>
        )}
        {/*
          Stated rather than implied. These stops are folded from ONE PAGE of matches, so a
          trail that continues past it is cut off here — and a cut-off trail that looks whole is
          the one way this view could mislead.
        */}
        {partial && (
          <span className="text-[11px] text-muted-foreground">
            · this page only ({rows.length} of {total} detections)
          </span>
        )}
        {onFollowTrail && (
          <button
            type="button"
            onClick={onFollowTrail}
            className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-brand hover:underline"
            title="Sessionize the whole match set server-side, one trail per subject"
          >
            <MapPinIcon className="h-3.5 w-3.5" />
            Full trail for every subject
          </button>
        )}
      </div>

      {undated > 0 && (
        <p className="mb-2 text-[11px] text-muted-foreground">
          {undated} {undated === 1 ? 'result has' : 'results have'} no usable timestamp and cannot
          be placed on a timeline — switch to the grid to see {undated === 1 ? 'it' : 'them'}.
        </p>
      )}

      {/*
        The explicit column count replaces `auto-fill` the moment there is a width to read — the
        same layout, but one whose row breaks JavaScript can see, which is what the connectors
        need. Before that, and anywhere unmeasurable, the auto-fill track renders the cards
        correctly with no ribbon drawn.
      */}
      <ol
        ref={trackRef}
        className={cols ? 'grid gap-y-2' : GRID_AUTO}
        style={cols ? {
          // Fixed-width columns, so a card is the width of its contents and not of the row's
          // leftovers. One column is the exception: on a phone there is nothing to the right of
          // the card to leave empty, so it takes the width.
          gridTemplateColumns: cols === 1 ? 'minmax(0, 1fr)' : `repeat(${cols}, ${stopCard.width}px)`,
          columnGap: gap,
        } : undefined}
      >
        {shown.map((stop, i) => {
          const next = shown[i + 1];
          const cameraLabel = label(stop.cameraId);
          const newDay = i === 0 || dayKey(stop.from) !== dayKey(shown[i - 1].from);
          const nextIsNewDay = !!next && dayKey(next.from) !== dayKey(stop.from);

          /*
           * The move that this stop is one end of, in the direction the page is being read.
           *
           * Reading oldest-first, the NEXT stop happened later, so the subject moved on to it.
           * Reading newest-first it happened earlier, so they came from it. Same two stops, two
           * true sentences — and writing the wrong one would reverse the trail while looking
           * perfectly plausible.
           *
           * Written on THIS card rather than in a gutter between two cards, because there is no
           * gutter in a wrapping layout: the stop after this one may be on the next line, or
           * three columns away, and a connector drawn between them would point at the wrong
           * card. A sentence that belongs to one stop survives being wrapped anywhere.
           */
          const gapSec = next
            ? Math.abs(order === 'asc' ? next.from - stop.to : stop.from - next.to) / 1000
            : 0;
          const movedCamera = next && next.cameraId !== stop.cameraId;
          const move: Move = next
            ? order === 'asc'
              ? {
                direction: 'forward',
                text: movedCamera
                  ? `to ${label(next.cameraId)} · ${fmtDuration(gapSec)} later`
                  : `seen here again · ${fmtDuration(gapSec)} later`,
              }
              : {
                direction: 'back',
                text: movedCamera
                  ? `from ${label(next.cameraId)} · ${fmtDuration(gapSec)} earlier`
                  : `also here · ${fmtDuration(gapSec)} earlier`,
              }
            // The end of the page is not the end of the trail, and saying "page" is what keeps
            // the two from being confused.
            : { direction: null, text: order === 'asc' ? 'last on this page' : 'earliest on this page' };

          /*
           * Where the ribbon goes next: along the row, or round the turn at the end of it.
           *
           * Neither, across a day boundary. The heading is already the break, and a trail drawn
           * through it would say the subject walked from one day into the next — which is true
           * of the clock and misleading about the movement, since what happened in between is
           * not on this page at all.
           */
          const linksOn = !!next && !nextIsNewDay && cols > 0;
          const turns = linksOn && rowEnds[i];

          return (
            <Fragment key={stop.key}>
              {/*
                A day boundary breaks the flow across the full width, so a stop at 23:58 and one
                at 00:03 cannot end up side by side reading as five minutes apart.
              */}
              {newDay && (
                <li className="col-span-full mt-4 flex list-none items-center gap-2 first:mt-0">
                  {/*
                    The day, as a heading rather than a caption. It was 10px of muted uppercase
                    sitting above a wall of frames, which is a label a reader has to go looking
                    for — and the one fact that decides what every card under it means. A chip
                    with a rule running off it reads as a section break at a glance, from across
                    the room, which is how this page is actually looked at.
                  */}
                  <span className="flex shrink-0 items-baseline gap-1.5 rounded-md border border-border bg-muted px-2 py-1">
                    <span className="text-xs font-semibold text-foreground">{dayLabel(stop.from)}</span>
                    {/* The calendar reading, whenever the label is a relative word. "Today" is
                        the useful name and the date is the one that can be written down; a
                        screenshot of this page should say which day it was. */}
                    {dayLabel(stop.from) !== dayDate(stop.from) && (
                      <span className="text-[11px] text-muted-foreground">{dayDate(stop.from)}</span>
                    )}
                  </span>
                  <span className="h-px flex-1 bg-border" aria-hidden="true" />
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {dayStops.get(dayKey(stop.from))}{' '}
                    {dayStops.get(dayKey(stop.from)) === 1 ? 'stop' : 'stops'}
                  </span>
                </li>
              )}

              <StopCard
                stop={stop}
                density={stopCard.density}
                cameraLabel={cameraLabel}
                /* A position only where it means something. An ordinal is a place on a trail,
                   and a page holding several subjects has no single trail to be on. */
                ordinal={oneSubject && stops.length > 1 ? stop.ordinal : null}
                move={move}
                subjectNote={identities.size > 1 && stop.tracked
                  ? (stop.subjectLabel
                    || (stop.subjectKind === 'vehicle' ? 'No plate read'
                      : stop.isKnown ? 'Enrolled' : 'Unidentified'))
                  : null}
                accent={stops.length > 1 && stop === firstInTime ? 'first'
                  : stops.length > 1 && stop === lastInTime ? 'last'
                    : null}
                imageFor={imageFor}
                loadImage={loadImage}
                onOpen={onOpen}
                link={linksOn && !turns ? { moved: !!movedCamera, text: move.text, gap } : null}
                order={order}
                onPlay={onPlay && (!playable || playable(stop.hits[0].row))
                  ? () => onPlay(stop.hits[0].row)
                  : undefined}
              />

              {turns && <WrapLink order={order} text={move.text} />}
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}
